import React, { useState, useEffect, useRef } from "react";
import { Card, Table, Input, Button, Typography, Select, Space, Tooltip } from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { ExcelImportExport } from "../../../common/ExcelImportExport";
import NumberInput from "../../../common/NumberInput";
import { ColumnConfigModal, selectStyles } from "../../../common/ColumnConfigModal";
import { COLORS, EVALUATE_CODES, EvaluateCode } from "../../../../utils/constants";
import { useTender } from "@/contexts/TenderContext";
import { addColumnCodes } from "@/utils/addColumnCodes";
import { DRAINAGE_REFERENCES } from "@/utils/drainageReferenceMapping";
import { normaliseDecimalMmInLabel } from "../../../../utils/dimensionLabel";
import { useTableRenderDeferral } from "../../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../../common/TableLoadingPlaceholder";

const { Title, Text } = Typography;
const { Option } = Select;

interface DrainageRow {
  key: string;
  plotNo?: string; // Optional - kept for backward compatibility
  housetype?: string; // Optional - kept for backward compatibility
  plots: number;
  apartments: number;
  nmePhic: number;
  yardGully: number;
  drainageChannel: number;
  landDrain: number;
  cellularAtten: number;
  polypipeFoul: number;
  polypipeStorm: number;
  osmaFoul: number;
  osmaStorm: number;
  // Additional fields for OSMA-FOUL
  gravel: number;
  concealedSetSurround: number;
  tipOnSite: number;
  osmaNmePpic: number;
  osma600mmNmePpic: number;
  // Additional fields for OSMA-STORM
  osmaStormGravel: number;
  osmaStormConcreteBedSurround: number;
  osmaStormTipOnsite: number;
  osmaStormNmePpic: number;
  osmaStorm600mmNmePpic: number;
  osmaStormYardGully: number;
  osmaStormDrainageChannel: number;
  osmaStormLandDrain: number;
  // Additional fields for POLYPIPE-FOUL
  polypipeGravel: number;
  polypipeConcreteBedSurround: number;
  polypipeTipOnSite: number;
  polypipeNmePpic: number;
  polypipe600mmNmePpic: number;
  // Additional fields for POLYPIPE-STORM
  polypipeStormGravel: number;
  polypipeStormConcreteBedSurround: number;
  polypipeStormTipOnSite: number;
  polypipeStormNmePpic: number;
  polypipeStorm600mmNmePpic: number;
  polypipeStormYardGully: number;
  polypipeStormDrainageChannel: number;
  polypipeStormLandDrain: number;
  // SW & S detailed fields for OSMA FOUL
  osma32mmBend: number;
  osmaPPIC: number;
  osma50mmBend: number;
  osma68mmBend: number;
  osma82mmBend: number;
  osma110mmBend: number;
  osma32mmPipe: number;
  osma40mmPipe: number;
  osma50mmPipe: number;
  osma68mmPipe: number;
  osma82mmPipe: number;
  osma110mmPipe: number;
  // SW & S detailed fields for OSMA STORM
  osmaStorm32mmBend: number;
  osmaStorPPIC: number;
  osmaStorm50mmBend: number;
  osmaStorm68mmBend: number;
  osmaStorm100100JunctionTripleSocket: number;
  osmaStorm82mmBend: number;
  osmaStorm110mmBend: number;
  osmaStorm32mmPipe: number;
  osmaStorm40mmPipe: number;
  osmaStorm50mmPipe: number;
  osmaStorm68mmPipe: number;
  osmaStorm82mmPipe: number;
  osmaStorm110mmPipe: number;
  // SW & S detailed fields for POLYPIPE FOUL
  polypipe32mmBend: number;
  polypipePPIC: number;
  polypipe50mmBend: number;
  polypipe68mmBend: number;
  polypipe110mmBend: number;
  polypipe32mmPipe: number;
  polypipe40mmPipe: number;
  polypipe50mmPipe: number;
  polypipe68mmPipe: number;
  polypipe82mmPipe: number;
  polypipe110mmPipe: number;
  // SW & S detailed fields for POLYPIPE STORM
  polypipeStorm32mmBend: number;
  polypipeStormPPIC: number;
  polypipeStorm50mmBend: number;
  polypipeStorm68mmBend: number;
  polypipeStorm100100JunctionTripleSocket: number;
  polypipeStorm82mmBend: number;
  polypipeStorm110mmBend: number;
  polypipeStorm32mmPipe: number;
  polypipeStorm40mmPipe: number;
  polypipeStorm50mmPipe: number;
  polypipeStorm68mmPipe: number;
  polypipeStorm82mmPipe: number;
  polypipeStorm110mmPipe: number;
}

interface StandardDrainageViewProps {
  data: DrainageRow[];
  selectedSupplier: "Osma" | "Polypipe";
  onSupplierChange: (value: "Osma" | "Polypipe") => void;
  onInputChange: (
    key: string,
    field: keyof DrainageRow,
    value: string | number,
  ) => void;
  onAddRow: () => void;
  onDeleteRow: (key: string) => void;
  onDeleteRows?: (keys: React.Key[]) => void;
  onExport: () => void;
  onImport: (file: File) => void;
  onDownloadTemplate: () => void;
  importLoading: boolean;
  importModalVisible: boolean;
  setImportModalVisible: (visible: boolean) => void;
  onSave?: () => void;
  onSaveAndContinue?: () => void;
  onSaveAndExit?: () => void;
  isViewMode?: boolean;
  columnHeaders?: Record<string, string>;
  dimensionValues?: Record<string, string>;
  onColumnHeadersChange?: (headers: Record<string, string>, dimensions: Record<string, string>) => void;
  polypipeDimensionValues?: Record<string, string>;
  onPolypipeConfigChange?: (values: Record<string, string>) => void;
}

const drainageColumnTitle = (headerText: React.ReactNode, unit: string, code?: string) => (
  <div className="text-center">
    <Tooltip title={typeof headerText === "string" ? headerText : undefined}>
      <div className="gw-header-title">{headerText}</div>
    </Tooltip>
    <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
    {code && <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2, lineHeight: 1.3, letterSpacing: '0.01em' }}>{code}</div>}
  </div>
);

const StandardDrainageView: React.FC<StandardDrainageViewProps> = ({
  data,
  selectedSupplier,
  onSupplierChange,
  onInputChange,
  onAddRow,
  onDeleteRow,
  onDeleteRows,
  onExport,
  onImport,
  onDownloadTemplate,
  importLoading,
  importModalVisible,
  setImportModalVisible,
  onSave,
  onSaveAndContinue,
  onSaveAndExit,
  isViewMode,
  columnHeaders: externalColumnHeaders,
  dimensionValues: externalDimensionValues,
  onColumnHeadersChange,
  polypipeDimensionValues: externalPolypipeDimensionValues,
  onPolypipeConfigChange,
}) => {
  const [activeTab, setActiveTab] = useState<string>("foul");
  const [selectedDrainageKeys, setSelectedDrainageKeys] = useState<React.Key[]>([]);
  const [columnHeaders, setColumnHeaders] = useState<Record<string, string>>(
    externalColumnHeaders || {}
  );
  const [dimensionValues, setDimensionValues] = useState<Record<string, string>>(
    externalDimensionValues || {}
  );
  const [tempColumnHeaders, setTempColumnHeaders] = useState<Record<string, string>>({});
  const [tempDimensionValues, setTempDimensionValues] = useState<Record<string, string>>({});
  const [polypipeDimValues, setPolypipeDimValues] = useState<Record<string, string>>(
    externalPolypipeDimensionValues || {}
  );
  const [tempPolypipeValues, setTempPolypipeValues] = useState<Record<string, string>>({});
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const prevConfigModalVisibleRef = useRef(configModalVisible);
  const [foulConfigModalVisible, setFoulConfigModalVisible] = useState(false);
  const prevFoulConfigModalVisibleRef = useRef(foulConfigModalVisible);
  // Defers the drainage table's mount by one paint cycle so a tender with a
  // lot of plots doesn't make this step look frozen while the table's
  // render/layout blocks the main thread - see useTableRenderDeferral's own
  // doc comment. Re-keyed on `activeTab`: this table stays mounted across
  // the foul/storm switch (only its `columns`/`dataSource` change via
  // `getCurrentTabColumns()`), but swapping to an entirely different column
  // set is itself expensive enough to want the same cover - so the loader
  // reappears on every tab switch, not just the initial mount. Purely a
  // perceived-lag fix: once it flips false the table renders exactly as
  // before.
  const isTableLoading = useTableRenderDeferral([activeTab]);

  const { state: tender } = useTender();

  // Reset temp values when either modal opens
  useEffect(() => {
    if (configModalVisible && !prevConfigModalVisibleRef.current) {
      setTempDimensionValues({ ...dimensionValues });
      setTempColumnHeaders({ ...columnHeaders });
    }
    prevConfigModalVisibleRef.current = configModalVisible;
  }, [configModalVisible, dimensionValues, columnHeaders]);

  useEffect(() => {
    if (foulConfigModalVisible && !prevFoulConfigModalVisibleRef.current) {
      setTempDimensionValues({ ...dimensionValues });
      setTempColumnHeaders({ ...columnHeaders });
      setTempPolypipeValues({ ...polypipeDimValues });
    }
    prevFoulConfigModalVisibleRef.current = foulConfigModalVisible;
  }, [foulConfigModalVisible, dimensionValues, columnHeaders, polypipeDimValues]);

  // Sync from external props
  useEffect(() => {
    if (externalColumnHeaders) setColumnHeaders(externalColumnHeaders);
  }, [externalColumnHeaders]);

  useEffect(() => {
    if (externalDimensionValues) setDimensionValues(externalDimensionValues);
  }, [externalDimensionValues]);

  useEffect(() => {
    if (externalPolypipeDimensionValues) setPolypipeDimValues(externalPolypipeDimensionValues);
  }, [externalPolypipeDimensionValues]);

  // Notify parent when headers change
  useEffect(() => {
    if (onColumnHeadersChange && (Object.keys(columnHeaders).length > 0 || Object.keys(dimensionValues).length > 0)) {
      onColumnHeadersChange(columnHeaders, dimensionValues);
    }
  }, [columnHeaders, dimensionValues]);

  const getStormColumnTitle = (fieldKey: string, defaultTitle: string): string => {
    // Return the stored column header (from Excel import) exactly as-is so the
    // table displays the same text as the imported sheet.
    return columnHeaders[fieldKey] || defaultTitle;
  };

  const getTabDisplayName = (tabKey: string, supplier: string) => {
    const tabNames = {
      foul: `${supplier} - FOUL`,
      storm: `${supplier} - STORM`,
    };
    return tabNames[tabKey as keyof typeof tabNames] || "Drainage";
  };

  // Plot No and Housetype columns removed as per user request
  const baseColumns: any[] = [];

  const createSupplierColumns = (
    supplier: "Polypipe" | "Osma",
    sectionType: "foul" | "storm",
  ) => {
    const fieldPrefix = supplier.toLowerCase();

    const createSectionColumns = () => {
      const fieldName =
        sectionType === "foul" ? fieldPrefix : `${fieldPrefix}Storm`;

      if (supplier === "Osma" && sectionType === "foul") {
        // OSMA-FOUL specific columns from the image - exact match
        return [
          {
            title: drainageColumnTitle("Lab per plot", "Nr"),
            dataIndex: `${fieldName}32mmBend`,
            key: `${fieldName}LabPerPlot`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}32mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("PPIC's", "Nr"),
            dataIndex: "osmaPPIC",
            key: `${fieldName}PPICs`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaPPIC" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Mini PPIC's", "Nr"),
            dataIndex: `${fieldName}50mmBend`,
            key: `${fieldName}MiniPPICs`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}50mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("SVP", "Nr"),
            dataIndex: `${fieldName}68mmBend`,
            key: `${fieldName}SVP`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}68mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Concrete to rest bends", "Nr"),
            dataIndex: `${fieldName}82mmBend`,
            key: `${fieldName}ConcreteRestBends`,
            width: 140,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}82mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100/100 junctions", "Nr"),
            dataIndex: `${fieldName}110mmBend`,
            key: `${fieldName}100100Junctions`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}110mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100mm pipe", "m"),
            dataIndex: `${fieldName}32mmPipe`,
            key: `${fieldName}100mmPipe`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}32mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100mm couplers", "Nr"),
            dataIndex: `${fieldName}40mmPipe`,
            key: `${fieldName}100mmCouplers`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}40mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100/150 junction", "Nr"),
            dataIndex: `${fieldName}50mmPipe`,
            key: `${fieldName}100150Junction`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}50mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150/150 junction", "Nr"),
            dataIndex: `${fieldName}68mmPipe`,
            key: `${fieldName}150150Junction`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}68mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150mm dia pipe", "m"),
            dataIndex: `${fieldName}82mmPipe`,
            key: `${fieldName}150mmDiaPipe`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}82mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150mm couplers", "Nr"),
            dataIndex: `${fieldName}110mmPipe`,
            key: `${fieldName}150mmCouplers`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}110mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Gravel", "m"),
            dataIndex: "gravel",
            key: `${fieldName}Gravel`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.gravel}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "gravel" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Concrete bed & surround", "m"),
            dataIndex: "concealedSetSurround",
            key: `${fieldName}ConcreteBedSurround`,
            width: 140,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.concealedSetSurround}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "concealedSetSurround" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Tip onsite", "m³"),
            dataIndex: "tipOnSite",
            key: `${fieldName}TipOnsite`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.tipOnSite}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "tipOnSite" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("NME PPIC", "Nr"),
            dataIndex: "osmaNmePpic",
            key: `${fieldName}NmePpic`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osmaNmePpic}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaNmePpic" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("600mm NME PPIC", "Nr"),
            dataIndex: "osma600mmNmePpic",
            key: `${fieldName}600mmNmePpic`,
            width: 120,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osma600mmNmePpic}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osma600mmNmePpic" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
        ];
      } else if (supplier === "Osma" && sectionType === "storm") {
        // OSMA-STORM specific columns from the image - exact match
        return [
          {
            title: drainageColumnTitle("Lab per plot", "Nr"),
            dataIndex: `${fieldName}32mmBend`,
            key: `${fieldName}StormLabPerPlot`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}32mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("PPIC's", "Nr"),
            dataIndex: "osmaStorPPIC",
            key: `${fieldName}StormPPICs`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStorPPIC" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Rodding Eye", "Nr"),
            dataIndex: `${fieldName}50mmBend`,
            key: `${fieldName}StormRoddingEye`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}50mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("RWP", "Nr"),
            dataIndex: `${fieldName}68mmBend`,
            key: `${fieldName}StormRWP`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}68mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100/100 Junction (Triple Socket)", "Nr"),
            dataIndex: "osmaStorm100100JunctionTripleSocket",
            key: `${fieldName}Storm100100JunctionTripleSocket`,
            width: 140,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStorm100100JunctionTripleSocket" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100mm pipe", "m"),
            dataIndex: `${fieldName}82mmBend`,
            key: `${fieldName}Storm100mmPipe`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}82mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100mm couplers", "Nr"),
            dataIndex: `${fieldName}110mmBend`,
            key: `${fieldName}Storm100mmCouplers`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}110mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100/150 junction", "Nr"),
            dataIndex: `${fieldName}32mmPipe`,
            key: `${fieldName}Storm100150Junction`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}32mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150/150 junction", "Nr"),
            dataIndex: `${fieldName}40mmPipe`,
            key: `${fieldName}Storm150150Junction`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}40mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150mm dia pipe", "m"),
            dataIndex: `${fieldName}50mmPipe`,
            key: `${fieldName}Storm150mmDiaPipe`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}50mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150mm couplers", "Nr"),
            dataIndex: `${fieldName}68mmPipe`,
            key: `${fieldName}Storm150mmCouplers`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}68mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Gravel", "m"),
            dataIndex: "osmaStormGravel",
            key: `${fieldName}StormGravel`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osmaStormGravel}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStormGravel" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Concrete bed & surround", "m"),
            dataIndex: "osmaStormConcreteBedSurround",
            key: `${fieldName}StormConcreteBedSurround`,
            width: 140,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osmaStormConcreteBedSurround}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStormConcreteBedSurround" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Tip onsite", "m³"),
            dataIndex: "osmaStormTipOnsite",
            key: `${fieldName}StormTipOnsite`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osmaStormTipOnsite}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStormTipOnsite" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("NME PPIC", "Nr"),
            dataIndex: "osmaStormNmePpic",
            key: `${fieldName}StormNmePpic`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osmaStormNmePpic}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStormNmePpic" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("600mm NME PPIC", "Nr"),
            dataIndex: "osmaStorm600mmNmePpic",
            key: `${fieldName}Storm600mmNmePpic`,
            width: 120,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osmaStorm600mmNmePpic}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStorm600mmNmePpic" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Yard Gully", "Nr"),
            dataIndex: "osmaStormYardGully",
            key: `${fieldName}StormYardGully`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osmaStormYardGully}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStormYardGully" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle(getStormColumnTitle("osmaStormDrainageChannel", "Drainage Channel"), "m"),
            dataIndex: "osmaStormDrainageChannel",
            key: `${fieldName}StormDrainageChannel`,
            width: 130,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osmaStormDrainageChannel}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStormDrainageChannel" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle(getStormColumnTitle("osmaStormLandDrain", "Land Drain"), "m"),
            dataIndex: "osmaStormLandDrain",
            key: `${fieldName}StormLandDrain`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={record.osmaStormLandDrain}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "osmaStormLandDrain" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
        ];
      } else if (supplier === "Polypipe" && sectionType === "foul") {
        // POLYPIPE-FOUL specific columns from the image - exact match
        return [
          {
            title: drainageColumnTitle("Lab per plot", "Nr"),
            dataIndex: `${fieldName}32mmBend`,
            key: `${fieldName}PolypipeFoulLabPerPlot`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}32mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("PPIC's", "Nr"),
            dataIndex: "polypipePPIC",
            key: `${fieldName}PolypipeFoulPPICs`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipePPIC" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Mini PPIC's", "Nr"),
            dataIndex: `${fieldName}50mmBend`,
            key: `${fieldName}PolypipeFoulMiniPPICs`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}50mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("SVP", "Nr"),
            dataIndex: `${fieldName}68mmBend`,
            key: `${fieldName}PolypipeFoulSVP`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}68mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Concrete to rest bends", "Nr"),
            dataIndex: `${fieldName}82mmBend`,
            key: `${fieldName}PolypipeFoulConcreteRestBends`,
            width: 140,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}82mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100/100 Junctions", "Nr"),
            dataIndex: `${fieldName}110mmBend`,
            key: `${fieldName}PolypipeFoul100100Junctions`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}110mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100mm pipe", "m"),
            dataIndex: `${fieldName}32mmPipe`,
            key: `${fieldName}PolypipeFoul100mmPipe`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}32mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100mm couplers", "Nr"),
            dataIndex: `${fieldName}40mmPipe`,
            key: `${fieldName}PolypipeFoul100mmCouplers`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}40mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100/150 junction", "Nr"),
            dataIndex: `${fieldName}50mmPipe`,
            key: `${fieldName}PolypipeFoul100150Junction`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}50mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150/150 junction", "Nr"),
            dataIndex: `${fieldName}68mmPipe`,
            key: `${fieldName}PolypipeFoul150150Junction`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}68mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150mm dia pipe", "m"),
            dataIndex: `${fieldName}82mmPipe`,
            key: `${fieldName}PolypipeFoul150mmDiaPipe`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}82mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150mm couplers", "Nr"),
            dataIndex: `${fieldName}110mmPipe`,
            key: `${fieldName}PolypipeFoul150mmCouplers`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}110mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Gravel", "m"),
            dataIndex: "polypipeGravel",
            key: `${fieldName}PolypipeFoulGravel`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeGravel" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Concrete bed & surround", "m"),
            dataIndex: "polypipeConcreteBedSurround",
            key: `${fieldName}PolypipeFoulConcreteBedSurround`,
            width: 140,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeConcreteBedSurround" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Tip onsite", "m³"),
            dataIndex: "polypipeTipOnSite",
            key: `${fieldName}PolypipeFoulTipOnsite`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeTipOnSite" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("NME PPIC", "Nr"),
            dataIndex: "polypipeNmePpic",
            key: `${fieldName}PolypipeFoulNmePpic`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeNmePpic" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("600mm NME PPIC", "Nr"),
            dataIndex: "polypipe600mmNmePpic",
            key: `${fieldName}PolypipeFoul600mmNmePpic`,
            width: 120,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipe600mmNmePpic" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
        ];
      } else if (supplier === "Polypipe" && sectionType === "storm") {
        // POLYPIPE-STORM specific columns from the image - exact match
        return [
          {
            title: drainageColumnTitle("Lab per plot", "Nr"),
            dataIndex: `${fieldName}32mmBend`,
            key: `${fieldName}PolypipeStormLabPerPlot`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}32mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("PPIC's", "Nr"),
            dataIndex: "polypipeStormPPIC",
            key: `${fieldName}PolypipeStormPPICs`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStormPPIC" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Rodding Eye", "Nr"),
            dataIndex: `${fieldName}50mmBend`,
            key: `${fieldName}PolypipeStormRoddingEye`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}50mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("RWP", "Nr"),
            dataIndex: `${fieldName}68mmBend`,
            key: `${fieldName}PolypipeStormRWP`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}68mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100/100 Junction (Triple Socket)", "Nr"),
            dataIndex: "polypipeStorm100100JunctionTripleSocket",
            key: `${fieldName}PolypipeStormTripleSocket`,
            width: 130,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStorm100100JunctionTripleSocket" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100mm pipe", "m"),
            dataIndex: `${fieldName}82mmBend`,
            key: `${fieldName}PolypipeStorm100mmPipe`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}82mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100mm couplers", "Nr"),
            dataIndex: `${fieldName}110mmBend`,
            key: `${fieldName}PolypipeStorm100mmCouplers`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}110mmBend` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("100/150 junction", "Nr"),
            dataIndex: `${fieldName}32mmPipe`,
            key: `${fieldName}PolypipeStorm100150Junction`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}32mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150/150 junction", "Nr"),
            dataIndex: `${fieldName}40mmPipe`,
            key: `${fieldName}PolypipeStorm150150Junction`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}40mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150mm dia pipe", "m"),
            dataIndex: `${fieldName}50mmPipe`,
            key: `${fieldName}PolypipeStorm150mmDiaPipe`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}50mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("150mm couplers", "Nr"),
            dataIndex: `${fieldName}68mmPipe`,
            key: `${fieldName}PolypipeStorm150mmCouplers`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    `${fieldName}68mmPipe` as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Gravel", "m"),
            dataIndex: "polypipeStormGravel",
            key: `${fieldName}PolypipeStormGravel`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStormGravel" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Concrete bed & surround", "m"),
            dataIndex: "polypipeStormConcreteBedSurround",
            key: `${fieldName}PolypipeStormConcreteBedSurround`,
            width: 140,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStormConcreteBedSurround" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Tip onsite", "m³"),
            dataIndex: "polypipeStormTipOnSite",
            key: `${fieldName}PolypipeStormTipOnsite`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStormTipOnSite" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("NME PPIC", "Nr"),
            dataIndex: "polypipeStormNmePpic",
            key: `${fieldName}PolypipeStormNmePpic`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStormNmePpic" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("600mm NME PPIC", "Nr"),
            dataIndex: "polypipeStorm600mmNmePpic",
            key: `${fieldName}PolypipeStorm600mmNmePpic`,
            width: 120,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStorm600mmNmePpic" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle("Yard Gully", "Nr"),
            dataIndex: "polypipeStormYardGully",
            key: `${fieldName}PolypipeStormYardGully`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStormYardGully" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle(getStormColumnTitle("polypipeStormDrainageChannel", "Drainage Channel"), "m"),
            dataIndex: "polypipeStormDrainageChannel",
            key: `${fieldName}PolypipeStormDrainageChannel`,
            width: 130,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStormDrainageChannel" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
          {
            title: drainageColumnTitle(getStormColumnTitle("polypipeStormLandDrain", "Land Drain"), "m"),
            dataIndex: "polypipeStormLandDrain",
            key: `${fieldName}PolypipeStormLandDrain`,
            width: 110,
            render: (value: number, record: DrainageRow) => (
              <NumberInput
                value={value}
                onChange={(newValue) =>
                  onInputChange(
                    record.key,
                    "polypipeStormLandDrain" as keyof DrainageRow,
                    newValue,
                  )
                }
                decimalPlaces={3}
                placeholder="0.000"
                size="small"
              />
            ),
          },
        ];
      } else {
        // Fallback - should not reach here
        return [];
      }
    };

    return createSectionColumns();
  };

  const createColumnsForTab = (tabType: "foul" | "storm") => [
    ...baseColumns,
    ...createSupplierColumns(selectedSupplier, tabType),
    {
      title: "Actions",
      key: "actions",
      width: 110,
      fixed: "right" as const,
      align: "center" as const,
      render: (_, record: DrainageRow) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => onDeleteRow(record.key)}
          size="small"
        />
      ),
    },
  ];

  const foulColumns = addColumnCodes(createColumnsForTab("foul"), DRAINAGE_REFERENCES);
  const stormColumns = addColumnCodes(createColumnsForTab("storm"), DRAINAGE_REFERENCES, dimensionValues);

  const calculateTotal = (field: keyof DrainageRow): number => {
    return data.reduce((sum, row) => sum + (Number(row[field]) || 0), 0);
  };

  const getCurrentTabColumns = () => {
    return activeTab === "foul" ? foulColumns : stormColumns;
  };

  // Calculate if horizontal and vertical scroll is needed
  const getScrollConfig = () => {
    const columns = getCurrentTabColumns();
    const totalWidth = columns.reduce((sum, col: any) => {
      // Calculate width, defaulting to 120 if not specified
      const width = col.width || 120;
      const childrenWidth = col.children
        ? col.children.reduce(
            (childSum: number, child: any) => childSum + (child.width || 120),
            0,
          )
        : 0;
      return sum + Math.max(width, childrenWidth);
    }, 0);

    // Calculate if vertical scroll is needed
    // Assuming each row is ~50px height, header ~60px, and max comfortable height is ~600px
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12; // ~600px of content
    const needsVerticalScroll = data.length > maxComfortableRows;

    const scrollConfig: any = { x: totalWidth > 1200 };

    // Only add vertical scroll if there are many rows
    if (needsVerticalScroll) {
      scrollConfig.y = maxComfortableRows * estimatedRowHeight;
    }

    return scrollConfig;
  };

  return (
    <>
      <Card
        className="shadow-sm drainage-card"
        bodyStyle={{ padding: 0 }}
        style={{ borderRadius: "8px", overflow: "hidden" }}
      >
        {/* Sticky Header with Title, Supplier, and Tabs */}
        <div
          className="drainage-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header Section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Drainage
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Detailed supply, waste & surface drainage configuration with
                  specific pipe sizes and bends.
                </Text>
              </div>
              <Button
                icon={<SettingOutlined />}
                onClick={() =>
                  activeTab === "storm"
                    ? setConfigModalVisible(true)
                    : setFoulConfigModalVisible(true)
                }
                type="default"
                className="configure-headers-btn"
                style={{
                  height: "32px",
                  borderRadius: "6px",
                  fontWeight: 500,
                  borderColor: "#1890ff",
                  color: "#1890ff",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  flexShrink: 0,
                  marginTop: "18px",
                }}
              >
                {activeTab === "storm" ? "Configure Headers" : "Configure Headers"}
              </Button>
            </div>
          </div>

          {/* Supplier and View Toggle */}
          <div
            className="px-6 py-4 flex items-center gap-6"
            style={{ borderBottom: "1px solid #e5e7eb" }}
          >
            <div className="flex items-center gap-2">
              <Text style={{ fontWeight: 500 }}>Supplier:</Text>
              <Select
                value={selectedSupplier}
                onChange={onSupplierChange}
                style={{ width: 200 }}
                className="supplier-select"
                disabled={isViewMode}
              >
                <Option value="Osma">Plot drainage - OSMA</Option>
                <Option value="Polypipe">Plot drainage - POLYPIPE</Option>
              </Select>
            </div>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {selectedDrainageKeys.length > 0 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (onDeleteRows) {
                      onDeleteRows(selectedDrainageKeys);
                    } else {
                      selectedDrainageKeys.forEach((key) => onDeleteRow(key as string));
                    }
                    setSelectedDrainageKeys([]);
                  }}
                  style={{ height: "32px", borderRadius: "6px", fontWeight: 500 }}
                >
                  Delete Selected ({selectedDrainageKeys.length})
                </Button>
              )}
            </div>
          </div>

          {/* Tab Buttons - Part of sticky header */}
          <div
            className="drainage-sticky-tabs px-6"
            style={{
              paddingTop: "24px",
              paddingBottom: "0px",
              display: "flex",
              gap: "4px",
              backgroundColor: "#fff",
            }}
          >
            <Button
              type={activeTab === "foul" ? "primary" : "default"}
              onClick={() => setActiveTab("foul")}
              className={
                activeTab === "foul"
                  ? "active-tab-button"
                  : "inactive-tab-button"
              }
              style={{
                backgroundColor:
                  activeTab === "foul" ? COLORS.primary : "#E1E6EF",
                borderColor: activeTab === "foul" ? COLORS.primary : "#E1E6EF",
                color: activeTab === "foul" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "foul" ? "none" : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "foul" ? 2 : 1,
              }}
            >
              {selectedSupplier} - FOUL
            </Button>
            <Button
              type={activeTab === "storm" ? "primary" : "default"}
              onClick={() => setActiveTab("storm")}
              className={
                activeTab === "storm"
                  ? "active-tab-button"
                  : "inactive-tab-button"
              }
              style={{
                backgroundColor:
                  activeTab === "storm" ? COLORS.primary : "#E1E6EF",
                borderColor: activeTab === "storm" ? COLORS.primary : "#E1E6EF",
                color: activeTab === "storm" ? "#fff" : "#6B7280",
                fontWeight: 500,
                borderRadius: "6px 6px 0 0",
                height: "38px",
                paddingLeft: "20px",
                paddingRight: "20px",
                borderBottom:
                  activeTab === "storm" ? "none" : "1px solid #E1E6EF",
                position: "relative",
                zIndex: activeTab === "storm" ? 2 : 1,
              }}
            >
              {selectedSupplier} - STORM
            </Button>
          </div>
        </div>

        {/* Content Section - Tables */}
        <div
          className="px-6 pb-6 drainage-content-section"
          style={{
            paddingTop: "0px",
            position: "relative",
            zIndex: 1,
            marginTop: "0px",
          }}
        >
          {/* Table Content */}
          {isTableLoading ? (
            <TableLoadingPlaceholder />
          ) : (
          <Table
            dataSource={data}
            columns={getCurrentTabColumns()}
            pagination={false}
            scroll={getScrollConfig()}
            sticky={{ offsetHeader: 390 }}
            size="middle"
            bordered
            className="drainage-table"
            rowSelection={{
              selectedRowKeys: selectedDrainageKeys,
              onChange: setSelectedDrainageKeys,
              fixed: "left",
              columnWidth: 48,
            }}
            summary={() => {
              if (data.length === 0) return null;

              const columns = getCurrentTabColumns();
              const cells = [];
              // Leading checkbox column added by rowSelection shifts every
              // real column's summary index by +1.
              let cellIndex = 1;

              cells.push(
                <Table.Summary.Cell key="select-cell" index={0} />,
              );

              // Show totals for all columns (no "Totals" label)
              columns.slice(0, -1).forEach((col, idx) => {
                if (col.children && col.children.length > 0) {
                  // Handle grouped columns (columns with children)
                  col.children.forEach((childCol) => {
                    if (childCol.dataIndex) {
                      cells.push(
                        <Table.Summary.Cell
                          key={`cell-${cellIndex}`}
                          index={cellIndex}
                          align="right"
                        >
                          <span
                            style={{
                              color: "#000",
                              textAlign: "right",
                              display: "block",
                              paddingRight: "8px",
                            }}
                          >
                            {calculateTotal(
                              childCol.dataIndex as keyof DrainageRow,
                            ).toFixed(3)}
                          </span>
                        </Table.Summary.Cell>,
                      );
                    } else {
                      cells.push(
                        <Table.Summary.Cell
                          key={`cell-${cellIndex}`}
                          index={cellIndex}
                          align="center"
                        >
                          <span style={{ color: "#000" }}>-</span>
                        </Table.Summary.Cell>,
                      );
                    }
                    cellIndex++;
                  });
                } else if (col.dataIndex) {
                  // Handle regular columns with dataIndex
                  cells.push(
                    <Table.Summary.Cell
                      key={`cell-${cellIndex}`}
                      index={cellIndex}
                      align="right"
                    >
                      <span
                        style={{
                          color: "#000",
                          textAlign: "right",
                          display: "block",
                          paddingRight: "8px",
                        }}
                      >
                        {calculateTotal(
                          col.dataIndex as keyof DrainageRow,
                        ).toFixed(3)}
                      </span>
                    </Table.Summary.Cell>,
                  );
                  cellIndex++;
                }
              });

              // Add empty cell for Actions column
              cells.push(
                <Table.Summary.Cell key="actions" index={cellIndex} />,
              );

              return (
                <Table.Summary fixed="bottom">
                  <Table.Summary.Row
                    style={{ backgroundColor: "#FFF7E6", fontWeight: 600 }}
                  >
                    {cells}
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
          />
          )}

          {/* Add New Row Button */}
          <div style={{ marginTop: "16px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Button
              type="text"
              icon={<PlusOutlined style={{ color: COLORS.primary }} />}
              onClick={onAddRow}
              style={{
                color: COLORS.primary,
                fontWeight: 500,
                padding: "4px 8px",
              }}
            >
              Add New Row
            </Button>
          </div>
        </div>

        {/* Footer Section */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={onSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={onSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>

        {/* Hidden ExcelImportExport */}
        <div style={{ display: "none" }}>
          <ExcelImportExport
            onExport={onExport}
            onImport={onImport}
            onDownloadTemplate={onDownloadTemplate}
            importLoading={importLoading}
            importModalVisible={importModalVisible}
            setImportModalVisible={setImportModalVisible}
            hasData={data.length > 0}
          />
        </div>
      </Card>

      <style>{`
        .drainage-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .drainage-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .drainage-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .drainage-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        .drainage-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .drainage-card .ant-card-body {
          overflow: visible !important;
        }

        .drainage-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        .drainage-table .ant-table-body {
          margin-top: 0 !important;
        }

        .active-tab-button {
          border-bottom: none !important;
        }

        .inactive-tab-button {
          border-bottom: none !important;
        }

        .drainage-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .drainage-table .ant-table-wrapper {
          margin-top: -1px !important;
        }

        .drainage-table .ant-table-container {
          margin-top: 0 !important;
        }

        .drainage-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .drainage-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .drainage-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 24px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          position: sticky !important;
          top: 390px !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .drainage-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }

        .drainage-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
          z-index: 99 !important;
        }

        /* The generic min-width:110px above also matches the rowSelection
           checkbox column since it's a plain th/td. Same as cut-fills: keep
           it pinned to the rowSelection columnWidth instead of being
           stretched. */
        .drainage-table .ant-table-thead > tr > th.ant-table-selection-column,
        .drainage-table .ant-table-tbody > tr > td.ant-table-selection-column {
          min-width: 48px !important;
          width: 48px !important;
        }

        .drainage-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .drainage-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .drainage-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        /* Bulk-select checkboxes should not highlight the row — keep the normal/hover backgrounds */
        .drainage-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }
        .drainage-table .ant-table-tbody > tr.ant-table-row-selected:hover > td {
          background-color: #fafafa !important;
        }

        .drainage-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        /* All columns are NumberInput components, so right-align all text inputs */
        .drainage-table .ant-input[type="text"] {
          text-align: right !important;
        }

        .drainage-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .drainage-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .drainage-table .ant-input-number {
          width: 100%;
        }

        .drainage-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .drainage-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .drainage-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .drainage-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      {/* Foul Configuration Modal */}
      <ColumnConfigModal
        title={selectedSupplier === "Osma" ? "Configure OSMA Foul" : "Configure Polypipe Foul"}
        open={foulConfigModalVisible}
        onClose={() => {
          setTempDimensionValues({ ...dimensionValues });
          setTempColumnHeaders({ ...columnHeaders });
          setTempPolypipeValues({ ...polypipeDimValues });
          setFoulConfigModalVisible(false);
        }}
        onApply={() => {
          if (selectedSupplier === "Osma") {
            setDimensionValues(tempDimensionValues);
            setColumnHeaders(tempColumnHeaders);
          } else {
            setPolypipeDimValues(tempPolypipeValues);
            if (onPolypipeConfigChange) onPolypipeConfigChange(tempPolypipeValues);
          }
        }}
        width={400}
        isViewMode={tender.isViewMode}
      >
        <div className="grid grid-cols-2 gap-3">
          {selectedSupplier === "Osma" ? (
            <>
              <div>
                <Typography.Text className="text-xs mb-1 block">Nr Plots Take off</Typography.Text>
                <NumberInput
                  value={Number(tempDimensionValues["foulNrPlotsTakeOff"] || 0)}
                  onChange={(val) =>
                    setTempDimensionValues((prev) => ({ ...prev, foulNrPlotsTakeOff: String(val) }))
                  }
                  isInteger
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <Typography.Text className="text-xs mb-1 block">Total Plots</Typography.Text>
                <NumberInput
                  value={Number(tempDimensionValues["foulTotalPlots"] || 0)}
                  onChange={(val) =>
                    setTempDimensionValues((prev) => ({ ...prev, foulTotalPlots: String(val) }))
                  }
                  isInteger
                  style={{ width: "100%" }}
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <Typography.Text className="text-xs mb-1 block">Nr Plots Take off</Typography.Text>
                <NumberInput
                  value={Number(tempPolypipeValues["nrPlotsTakeOff"] || 0)}
                  onChange={(val) =>
                    setTempPolypipeValues((prev) => ({ ...prev, nrPlotsTakeOff: String(val) }))
                  }
                  isInteger
                  style={{ width: "100%" }}
                />
              </div>
              <div>
                <Typography.Text className="text-xs mb-1 block">Total Plots</Typography.Text>
                <NumberInput
                  value={Number(tempPolypipeValues["totalPlots"] || 0)}
                  onChange={(val) =>
                    setTempPolypipeValues((prev) => ({ ...prev, totalPlots: String(val) }))
                  }
                  isInteger
                  style={{ width: "100%" }}
                />
              </div>
            </>
          )}
        </div>
      </ColumnConfigModal>

      {/* Column Configuration Modal for Storm Dynamic Headers */}
      <ColumnConfigModal
        title="Configure Storm Headers"
        open={configModalVisible}
        onClose={() => {
          setTempDimensionValues({ ...dimensionValues });
          setTempColumnHeaders({ ...columnHeaders });
          setConfigModalVisible(false);
        }}
        onApply={() => {
          setDimensionValues(tempDimensionValues);
          setColumnHeaders(tempColumnHeaders);
        }}
        isViewMode={tender.isViewMode}
      >
        <div className="grid grid-cols-2 gap-3">
          {/* Drainage Channel */}
          <div>
            <Typography.Text className="text-xs mb-1 block">Drainage Channel (m)</Typography.Text>
            <select
              value={
                selectedSupplier === "Osma"
                  ? tempDimensionValues["osmaStormDrainageChannel"] || ""
                  : tempDimensionValues["polypipeStormDrainageChannel"] || ""
              }
              onChange={(e) => {
                const value = e.target.value;
                const key = selectedSupplier === "Osma"
                  ? "osmaStormDrainageChannel"
                  : "polypipeStormDrainageChannel";
                if (!value) {
                  setTempDimensionValues((prev) => {
                    const newValues = { ...prev };
                    delete newValues[key];
                    return newValues;
                  });
                  setTempColumnHeaders((prev) => {
                    const newHeaders = { ...prev };
                    delete newHeaders[key];
                    return newHeaders;
                  });
                } else {
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    [key]: value,
                  }));
                  const selectedCode = value as EvaluateCode;
                  const label = EVALUATE_CODES[selectedCode]?.label || value;
                  setTempColumnHeaders((prev) => ({
                    ...prev,
                    [key]: label,
                  }));
                }
              }}
              style={selectStyles}
            >
              <option value="">-- Select --</option>
              <option value="GWALPDD020">Drainage channel</option>
              <option value="GWALPDD059">A15 Drainage channel</option>
              <option value="GWALPDD060">B125 Drainage channel</option>
            </select>
          </div>

          {/* Land Drain */}
          <div>
            <Typography.Text className="text-xs mb-1 block">Land Drain (m)</Typography.Text>
            <select
              value={
                selectedSupplier === "Osma"
                  ? tempDimensionValues["osmaStormLandDrain"] || ""
                  : tempDimensionValues["polypipeStormLandDrain"] || ""
              }
              onChange={(e) => {
                const value = e.target.value;
                const key = selectedSupplier === "Osma"
                  ? "osmaStormLandDrain"
                  : "polypipeStormLandDrain";
                if (!value) {
                  setTempDimensionValues((prev) => {
                    const newValues = { ...prev };
                    delete newValues[key];
                    return newValues;
                  });
                  setTempColumnHeaders((prev) => {
                    const newHeaders = { ...prev };
                    delete newHeaders[key];
                    return newHeaders;
                  });
                } else {
                  setTempDimensionValues((prev) => ({
                    ...prev,
                    [key]: value,
                  }));
                  const selectedCode = value as EvaluateCode;
                  const label = EVALUATE_CODES[selectedCode]?.label || value;
                  setTempColumnHeaders((prev) => ({
                    ...prev,
                    [key]: label,
                  }));
                }
              }}
              style={selectStyles}
            >
              <option value="">-- Select --</option>
              <option value="GWALPDD056">Land drainage</option>
              <option value="GWALPDD057">Connection to drainage system</option>
              <option value="L&P3">Silt trap chamber</option>
            </select>
          </div>
        </div>
      </ColumnConfigModal>
    </>
  );
};

export default StandardDrainageView;
